import {
  Children,
  CSSProperties,
  ComponentProps,
  ComponentType,
  ElementType,
  forwardRef,
  HTMLAttributes,
  isValidElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useRef,
  useEffect,
} from 'react';

import { DraggableAttributes } from '@dnd-kit/core';
import { DndContext } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Placement, autoUpdate, flip, size, useFloating } from '@floating-ui/react-dom';
import { Menu as HeadlessMenu, Portal } from '@headlessui/react';
import { HTMLMotionProps, motion } from 'framer-motion';
import styled from 'styled-components';

import { PortalDragOverlay } from '@components/drag-and-drop';
import { OverlayContainer, useSortableDefaults } from '@components/drag-and-drop/defaults';
import { FixedFullscreenOverlay } from '@components/fixed-fullscreen-overlay';
import { IconProps } from '@components/icons';
import { IconDrag } from '@components/icons/drag';
import { IconDropRight } from '@components/icons/dropright';
import { Link, LinkProps } from '@components/link';
import {
  handlePopoverSizeChange,
  moveUpMiddleware,
} from '@components/popover-button/popover-button';
import { Stack } from '@components/stack';
import { Switch, SwitchProps } from '@components/switch';
import { Text } from '@components/text';
import { dropdownMotionConfig } from '@styles/animation';
import { theme } from '@styles/theme';
import { scrollIntoView } from '@utils/browser';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { StrictUnion } from '@utils/types';

import { Button, ButtonProps } from '../button';
import { Tooltip } from '../tooltip';

export const HEADLESS_PORTAL_ID = 'headlessui-portal-id';

const extractSortableMenuItemsData = (
  content: ReactNode
): Record<string, { label: ReactNode; icon?: ReactElement<IconProps> }> => {
  const data: Record<string, { label: ReactNode; icon?: ReactElement<IconProps> }> = {};

  const extractFromChildren = (childrenToProcess: ReactNode) => {
    Children.forEach(childrenToProcess, (child) => {
      if (isValidElement(child)) {
        if (child.type === MenuItem || (child.type as ComponentType)?.displayName === 'MenuItem') {
          const props = child.props as MenuItemProps;
          if (props.sortable && props.id) {
            data[props.id] = {
              label: props.label,
              icon: props.icon,
            };
          }
        }
        if (child.props?.children) {
          extractFromChildren(child.props.children);
        }
      }
    });
  };

  if (content) {
    extractFromChildren(content);
  }

  return data;
};

const reorderSortableMenuChildren = (content: ReactNode, orderedIds: string[]): ReactNode => {
  const children = Children.toArray(content);

  const sortableByPosition: { index: number; id: string; element: ReactElement }[] = [];

  children.forEach((child, index) => {
    if (isValidElement(child)) {
      const props = child.props as MenuItemProps;
      if (props.sortable && props.id) {
        sortableByPosition.push({ index, id: props.id, element: child });
      }
    }
  });

  if (sortableByPosition.length <= 1) {
    return content;
  }

  const result = [...children];
  const positions = sortableByPosition.map((sortable) => sortable.index);
  const sortedElements = orderedIds
    .map((id) => sortableByPosition.find((sortable) => sortable.id === id)?.element)
    .filter((element): element is ReactElement => Boolean(element));

  positions.forEach((position, index) => {
    if (sortedElements[index]) {
      result[position] = sortedElements[index];
    }
  });

  return result;
};

type SortableMenuItemsProps = {
  onDragEnd: (newOrder: Array<string>) => void;
  menuContent: ReactNode;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyle: CSSProperties;
  motionProps?: HTMLMotionProps<'div'>;
  maxWidth?: number | string;
  width?: number | string;
  maxHeight?: number;
};

const SortableMenuItems = ({
  onDragEnd,
  menuContent,
  floatingRef,
  floatingStyle,
  motionProps,
  maxWidth,
  maxHeight,
  width,
}: SortableMenuItemsProps) => {
  const menuItemsData = useMemo(() => extractSortableMenuItemsData(menuContent), [menuContent]);
  const menuItems = useMemo(
    () => Object.entries(menuItemsData).map(([id, data]) => ({ id, ...data })),
    [menuItemsData]
  );

  const {
    items,
    itemIds,
    activeItem,
    handleDragEnd,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortableDefaults({ items: menuItems, onDragEnd });

  const orderedIds = itemIds.map(String);

  return (
    <DndContext
      {...sortingDefaults}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <StyledMenuItems
        ref={floatingRef}
        style={floatingStyle}
        {...motionProps}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
        $width={width}
      >
        <HeadlessMenu.Items static style={{ outline: 'none' }}>
          <SortableContext items={itemIds} strategy={strategy}>
            {reorderSortableMenuChildren(menuContent, orderedIds)}
          </SortableContext>
        </HeadlessMenu.Items>
      </StyledMenuItems>
      <PortalDragOverlay dropAnimation={null}>
        {activeItem && (
          <OverlayContainer>
            <MenuOverlayItem label={activeItem.label} icon={activeItem.icon} />
          </OverlayContainer>
        )}
      </PortalDragOverlay>
    </DndContext>
  );
};

interface MenuPropsBase extends Omit<HTMLAttributes<HTMLDivElement>, 'onDragEnd'> {
  children: ReactNode | (({ close }: { close(): void }) => ReactNode);
  maxWidth?: number | string;
  width?: number | string;
  placement?: Placement;
  motionProps?: HTMLMotionProps<'div'>;
  maxHeight?: number;
  dataTestid?: string;
}

export type MenuProps<T extends ElementType = typeof Button> = {
  skipPortalWrapper?: boolean;
  onDragEnd?: (newOrder: Array<string>) => void;
} & (MenuPropsBase &
  StrictUnion<
    | {
        buttonProps: ButtonProps;
      }
    | {
        trigger: T;
        triggerProps?: ComponentProps<T>;
      }
  >);

export function Menu<T extends ElementType = typeof Button>(props: MenuProps<T>) {
  const {
    children,
    placement = 'bottom-end',
    maxWidth,
    width,
    motionProps = dropdownMotionConfig,
    skipPortalWrapper = false,
    maxHeight,
    dataTestid,
    className,
    onDragEnd,
    ...rest
  } = props;

  const isSortable = Boolean(onDragEnd);

  const {
    x,
    y,
    refs,
    strategy: floatingStrategy,
  } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({
        fallbackPlacements: ['left-start', 'right-start'],
      }),
      moveUpMiddleware,
      size({
        apply: handlePopoverSizeChange,
      }),
    ],
  });

  const trigger = props.buttonProps ? Button : props.trigger;
  const triggerProps = props.buttonProps || props.triggerProps;

  const menuItems = (close: () => void) => {
    const content = typeof children === 'function' ? children({ close }) : children;

    if (isSortable) {
      return (
        <SortableMenuItems
          onDragEnd={onDragEnd!}
          menuContent={content}
          floatingRef={refs.setFloating}
          floatingStyle={{
            position: floatingStrategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          motionProps={motionProps}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          width={width}
        />
      );
    }

    return (
      <StyledMenuItems
        ref={refs.setFloating}
        style={{
          position: floatingStrategy,
          top: y ?? 0,
          left: x ?? 0,
        }}
        {...motionProps}
        $maxWidth={maxWidth}
        $maxHeight={maxHeight}
        $width={width}
      >
        <HeadlessMenu.Items static style={{ outline: 'none' }}>
          {content}
        </HeadlessMenu.Items>
      </StyledMenuItems>
    );
  };

  return (
    <MenuContainer {...rest} className={classNames(className, 'menu-container')}>
      <HeadlessMenu>
        {({ open, close }) => (
          <>
            <span
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <HeadlessMenu.Button
                className="menu-button"
                // (@siggirh): Seems like the ForwardRefExoticComponent is causing issues here
                as={trigger as ComponentType<ButtonProps>}
                ref={refs.setReference}
                {...triggerProps}
                data-active={open}
                data-testid={dataTestid}
              />
            </span>
            {open &&
              (skipPortalWrapper ? (
                menuItems(close)
              ) : (
                <Portal>
                  <div id={HEADLESS_PORTAL_ID}>
                    {menuItems(close)}
                    <FixedFullscreenOverlay onClick={close} />
                  </div>
                </Portal>
              ))}
          </>
        )}
      </HeadlessMenu>
    </MenuContainer>
  );
}

const MenuContainer = styled.div({
  position: 'relative',
});

export const MenuSeparator = () => <StyledMenuSeparator aria-hidden />;

type MenuItemContentProps = {
  label: ReactNode;
  icon?: ReactElement<IconProps>;
  extraIcon?: ReactElement<IconProps>;
  extraIconFullOpacity?: boolean;
  iconRight?: ReactNode;
  helpText?: string;
  isDisabled?: boolean;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners?: SyntheticListenerMap;
    disabled?: boolean;
  };
};

const MenuItemContent = ({
  label,
  icon,
  extraIcon,
  extraIconFullOpacity,
  iconRight,
  helpText,
  isDisabled,
  dragHandleProps,
}: MenuItemContentProps) => {
  return (
    <>
      <StyledMenuItem $helpText={helpText} data-disabled={isDisabled}>
        {dragHandleProps && (
          <DragHandleContainer
            {...(dragHandleProps.disabled
              ? {}
              : { ...dragHandleProps.attributes, ...dragHandleProps.listeners })}
            data-disabled={dragHandleProps.disabled}
          >
            <IconDrag color="elementPurple80" />
          </DragHandleContainer>
        )}
        {icon && <MenuItemIcon>{icon}</MenuItemIcon>}
        <LabelContainer>
          <Text font="regular14" text={label} ellipsisWithTooltip />
          {iconRight}
        </LabelContainer>
        {extraIcon && (
          <RightMenuItemIcon data-full-opacity={Boolean(extraIconFullOpacity)}>
            {extraIcon}
          </RightMenuItemIcon>
        )}
      </StyledMenuItem>
      {helpText && <HelpText>{helpText}</HelpText>}
    </>
  );
};

type CreateMenuItemHandlersProps = {
  onSelect?: (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  isDisabled: boolean;
  selectionType: 'close' | 'open' | 'through';
  isSubmenu: boolean;
};

const createMenuItemHandlers = ({
  onSelect,
  isDisabled,
  selectionType,
  isSubmenu,
}: CreateMenuItemHandlersProps) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!isDisabled) {
      if (selectionType === 'open') {
        e.preventDefault();
      }
      onSelect?.(e);
    } else {
      e.preventDefault();
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDisabled) {
      onSelect?.(e);
    }
  };

  if (isSubmenu) {
    return {
      // This is a shitfix, since having a nested menu seems to break the menu item selection on the submenu.
      // I realize it doesn't work well with keyboard, but it doesn't anyway in the submenu, so leaving this as is for now -- klemmi
      onMouseUp: handleMouseUp,
    };
  }

  return {
    onClick: handleClick,
  };
};

export type MenuItemProps = {
  onSelect?: (event: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => void;
  label: ReactNode;
  icon?: ReactElement<IconProps>;
  extraIcon?: ReactElement<IconProps>;
  extraIconFullOpacity?: boolean;
  iconRight?: ReactNode;
  isDisabled?: boolean;
  variant?: 'base' | 'danger' | 'muted';
  selectionType?: 'close' | 'open' | 'through';
  isActive?: boolean;
  isHeader?: boolean;
  tooltip?: string;
  dataCy?: string;
  dataTestId?: string;
  helpText?: string;
  id?: string;
  sortable?: boolean;
  isSortDisabled?: boolean;
  isSubmenu?: boolean;
};

export const MenuItem = ({
  onSelect,
  label,
  icon,
  iconRight = null,
  isDisabled = false,
  isHeader = false,
  variant = 'base',
  selectionType = 'close',
  isActive = false,
  extraIcon,
  extraIconFullOpacity = false,
  tooltip,
  dataCy,
  helpText,
  id,
  sortable = false,
  isSortDisabled = false,
  isSubmenu = false,
}: MenuItemProps) => {
  const selectedMenuItemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = selectedMenuItemRef.current;
    if (isActive && element) {
      setTimeout(() => {
        scrollIntoView(element, { block: 'nearest' });
      });
    }
  }, [isActive]);

  if (sortable && id) {
    return (
      <SortableMenuItem
        id={id}
        isSortDisabled={isSortDisabled}
        {...{
          onSelect,
          label,
          icon,
          iconRight,
          isDisabled,
          isHeader,
          variant,
          selectionType,
          isActive,
          extraIcon,
          extraIconFullOpacity,
          tooltip,
          dataCy,
          helpText,
          isSubmenu,
        }}
      />
    );
  }

  const handlers = createMenuItemHandlers({
    onSelect,
    isDisabled,
    selectionType,
    isSubmenu,
  });

  return (
    <Tooltip content={tooltip}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <HeadlessMenu.Item>
          {({ active }) => (
            <StyledMenuItemContainer
              ref={isActive ? selectedMenuItemRef : undefined}
              data-header={isHeader}
              data-active={isActive}
              data-variant={variant}
              data-disabled={isDisabled}
              data-selected={active}
              data-cy={dataCy}
              $helpText={helpText}
              {...handlers}
            >
              <MenuItemContent
                label={label}
                icon={icon}
                extraIcon={extraIcon}
                extraIconFullOpacity={extraIconFullOpacity}
                iconRight={iconRight}
                helpText={helpText}
                isDisabled={isDisabled}
              />
            </StyledMenuItemContainer>
          )}
        </HeadlessMenu.Item>
      </motion.div>
    </Tooltip>
  );
};

const SortableMenuItem = ({
  id,
  onSelect,
  label,
  icon,
  iconRight = null,
  isDisabled = false,
  isHeader = false,
  variant = 'base',
  selectionType = 'close',
  isActive = false,
  extraIcon,
  extraIconFullOpacity = false,
  tooltip,
  dataCy,
  helpText,
  isSubmenu = false,
  isSortDisabled = false,
}: MenuItemProps & { id: string }) => {
  const selectedMenuItemRef = useRef<HTMLDivElement>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isSortDisabled,
  });

  useEffect(() => {
    const element = selectedMenuItemRef.current;
    if (isActive && element) {
      setTimeout(() => {
        scrollIntoView(element, { block: 'nearest' });
      });
    }
  }, [isActive]);

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging && { opacity: 0 }),
  };

  const handlers = createMenuItemHandlers({
    onSelect,
    isDisabled,
    selectionType,
    isSubmenu,
  });

  return (
    <Tooltip content={tooltip}>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div ref={setNodeRef} style={style}>
          <HeadlessMenu.Item>
            {({ active }) => (
              <StyledMenuItemContainer
                ref={isActive ? selectedMenuItemRef : undefined}
                data-header={isHeader}
                data-active={isActive}
                data-variant={variant}
                data-disabled={isDisabled}
                data-selected={active}
                data-cy={dataCy}
                $helpText={helpText}
                {...handlers}
              >
                <MenuItemContent
                  label={label}
                  icon={icon}
                  extraIcon={extraIcon}
                  extraIconFullOpacity={extraIconFullOpacity}
                  iconRight={iconRight}
                  helpText={helpText}
                  isDisabled={isDisabled}
                  dragHandleProps={{ attributes, listeners, disabled: isSortDisabled }}
                />
              </StyledMenuItemContainer>
            )}
          </HeadlessMenu.Item>
        </div>
      </motion.div>
    </Tooltip>
  );
};

type MenuItemLinkProps = MenuItemProps & { isExternal?: boolean } & Pick<LinkProps, 'to'>;

export const MenuItemLink = ({ to, isExternal, ...rest }: MenuItemLinkProps) => {
  return (
    <Link to={to} {...(isExternal ? { target: '_blank' } : {})}>
      <MenuItem {...rest} />
    </Link>
  );
};

const LabelContainer = styled.div({
  display: 'flex',
  flex: 1,
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const HelpText = styled.div({
  font: theme.font.regular14,
  color: theme.color.textHelper,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const MenuLabel = ({
  label,
  icon,
}: {
  label: ReactNode;
  icon?: ReactElement<IconProps>;
}) => {
  if (icon) {
    return (
      <StyledMenuLabel>
        <Stack space={2} orientation="horizontal">
          {icon}
          {label}
        </Stack>
      </StyledMenuLabel>
    );
  }
  return <StyledMenuLabel>{label}</StyledMenuLabel>;
};

export const MenuDescription = ({ text, info = true }: { text: ReactNode; info?: boolean }) => {
  return <StyledMenuDescription info={info}>{text}</StyledMenuDescription>;
};

type MenuSwitchProps = Omit<MenuItemProps, 'iconRight' | 'selectionType' | 'onSelect'> &
  Pick<SwitchProps, 'value' | 'onChange' | 'size'>;

const MenuSwitch = ({ onChange, value, isDisabled, size = 'md', ...rest }: MenuSwitchProps) => {
  const handleChange = () => {
    onChange?.(!value);
  };
  return (
    <Menu.Item
      iconRight={
        <Switch
          label=""
          size={size}
          value={value}
          isDisabled={isDisabled}
          onChange={isDisabled ? undefined : onChange}
        />
      }
      selectionType="open"
      onSelect={isDisabled ? undefined : handleChange}
      isDisabled={isDisabled}
      {...rest}
    />
  );
};

type SubmenuTriggerProps = HTMLAttributes<HTMLDivElement> & {
  label: ReactNode;
  icon?: ReactElement<IconProps>;
};

const SubmenuTrigger = forwardRef<HTMLDivElement, SubmenuTriggerProps>(
  ({ label, icon, ...props }, ref) => {
    return (
      <StyledSubmenuTrigger ref={ref} {...props}>
        <StyledMenuItem>
          {icon && <MenuItemIcon>{icon}</MenuItemIcon>}
          <LabelContainer>
            <Text font="regular14" color="textPrimary" text={label} ellipsis />
          </LabelContainer>
          <IconDropRight size={16} color="elementPurple80" />
        </StyledMenuItem>
      </StyledSubmenuTrigger>
    );
  }
);

SubmenuTrigger.displayName = 'SubmenuTrigger';

const StyledSubmenuTrigger = styled.div({
  minHeight: theme.base.height,
  padding: `0 ${pxToRem(8)}`,
  borderRadius: pxToRem(4),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.color.uiTrans5,
  },
});

export type MenuSubmenuProps = {
  label: ReactNode;
  icon?: ReactElement<IconProps>;
  children: ReactNode;
  width?: number | string;
};

const MenuSubmenu = ({ label, icon, children, width }: MenuSubmenuProps) => {
  return (
    <Menu
      placement="right-start"
      trigger={SubmenuTrigger}
      triggerProps={{ label, icon }}
      width={width}
    >
      {children}
    </Menu>
  );
};

Menu.Description = MenuDescription;
Menu.Item = MenuItem;
Menu.ItemLink = MenuItemLink;
Menu.Separator = MenuSeparator;
Menu.Label = MenuLabel;
Menu.Switch = MenuSwitch;
Menu.Submenu = MenuSubmenu;

const StyledMenuLabel = styled.div({
  padding: '0.5rem',
  font: theme.font.semibold14,
  color: theme.color.textPrimary,
  textTransform: 'capitalize',
});

const StyledMenuDescription = styled.div<{
  info: boolean;
}>((p) => ({
  padding: '0.5rem',
  font: theme.font.regular14,
  color: p.info ? theme.color.textHelper : theme.color.textPrimary,
  whiteSpace: 'break-spaces',
}));

const StyledMenuSeparator = styled.div({
  height: '0.0625rem',
  backgroundColor: theme.color.border10,
  width: '100%',
  margin: `${pxToRem(8)} 0`,
});

const MenuItemIcon = styled.div({
  height: '1.5rem',
  width: '1.5rem',
  flexShrink: 0,
  '--icon-color': theme.color.elementPurple80,
});
const RightMenuItemIcon = styled(MenuItemIcon)({
  height: 'auto',
  width: 'auto',
});

const StyledMenuItems = styled(motion.div)<{
  $maxWidth?: MenuProps['maxWidth'];
  $maxHeight?: MenuProps['maxHeight'];
  $width?: MenuProps['width'];
}>((p) => ({
  position: 'relative',
  overflow: 'auto',
  backgroundColor: theme.color.backgroundLightDark,
  boxShadow: theme.shadow.high,
  zIndex: 10000,
  padding: '1rem',
  borderRadius: '0.25rem',
  outline: 'none',
  width: p.$width || '16rem',
  maxWidth: p.$maxWidth,
  maxHeight: p.$maxHeight,
  whiteSpace: 'nowrap',
}));

const StyledMenuItemContainer = styled.div<{
  $helpText?: MenuItemProps['helpText'];
}>((p) => ({
  minHeight: theme.base.height,
  font: p.$helpText ? theme.font.semibold14 : theme.font.regular14,
  color: theme.color.textPrimary,
  padding: p.$helpText ? '0.5rem' : '0 0.5rem',
  borderRadius: '0.25rem',
  '&[data-variant="danger"]': {
    color: theme.color.red100,
    '& span': { color: 'inherit' },
    '& div': { '--icon-color': theme.color.red100 },
  },

  '&[data-selected="true"]': {
    outline: 'none',

    '&:not([data-variant="muted"])': {
      cursor: 'pointer',
    },

    '&[data-variant="base"]': {
      backgroundColor: theme.color.uiTrans5,
    },

    '&[data-variant="danger"]': {
      backgroundColor: theme.color.redTrans10,
    },
  },

  '&[data-disabled="true"]': {
    cursor: 'not-allowed !important',
  },

  '&[data-active="true"]': {
    color: theme.color.elementBlue100,
    '& span': {
      color: `${theme.color.elementBlue100} !important`,
    },
  },

  '&[data-header="true"]': {
    font: theme.font.bold14,
    color: theme.color.textPrimary,
    textTransform: 'capitalize',
  },
}));

const StyledMenuItem = styled.div<{
  $helpText?: MenuItemProps['helpText'];
}>((p) => ({
  minHeight: p.$helpText ? 'auto' : theme.base.height,
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  '& > div': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '[data-disabled="true"] &': {
    '& > div:not([data-full-opacity="true"])': {
      opacity: 0.5,
    },
  },
}));

const DragHandleContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'grab',
  height: pxToRem(24),
  width: pxToRem(24),
  flexShrink: 0,
  '&:active': {
    cursor: 'grabbing',
  },
  '&:hover': {
    opacity: 0.8,
  },
  '&[data-disabled="true"]': {
    cursor: 'default',
    opacity: 0.5,
    '&:active': {
      cursor: 'default',
    },
  },
});

const MenuOverlayItem = ({ label, icon }: { label: ReactNode; icon?: ReactElement<IconProps> }) => {
  return (
    <StyledMenuItemContainer>
      <StyledMenuItem>
        {icon && <MenuItemIcon>{icon}</MenuItemIcon>}
        <LabelContainer>{label}</LabelContainer>
      </StyledMenuItem>
    </StyledMenuItemContainer>
  );
};
